// ============================
// File: src/pages/AoAPage.jsx
// ============================
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { AoAWorkbench } from "../features/aoa";
import DemandPageHeader from "../components/DemandPageHeader";
import DemandSelector from "../components/DemandSelector";
import { useDemandId } from "../context/DemandContext";
import { get } from "../api/http";

/**
 * Analysis of Alternatives stage — between Discovery and Enhanced Discovery (POC).
 *
 * MOCK: the analysis itself is demonstration data (features/aoa/mockAoa.js).
 * There is no AOA value in the backend stage sequence, so the selector lists
 * demands in Discovery (the ones about to need an AoA) and falls back to all.
 *
 * @param {(demandId: string) => void} [onAdvance] - optional; Dashboard.js can
 *   pass this to switch activeTab to "poc" instead of navigating to /poc/:id.
 */
export default function AoAPage({ onAdvance }) {
  const { demandId, setDemandId } = useDemandId();
  const params = useParams();
  const [title, setTitle] = useState("");

  // DemandContext only parses known stage paths from the URL and doesn't know
  // /aoa/:demandId, so seed the selection from the route param here.
  useEffect(() => {
    if (params.demandId && params.demandId !== demandId) setDemandId(params.demandId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.demandId]);

  // Demand title for the recommendation text; failure just omits it.
  useEffect(() => {
    if (!demandId) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const result = await get("/demands/list", { query: { limit: 200 } });
        const d = (result?.demands || []).find((x) => x.demand_id === demandId);
        if (!cancelled) setTitle(d?.title || "");
      } catch {
        if (!cancelled) setTitle("");
      }
    })();
    return () => { cancelled = true; };
  }, [demandId]);

  const header = (
    <DemandPageHeader
      title="Analysis of Alternatives"
      description="Compare candidate solutions on cost, benefit, risk, schedule and fit before the POC tests the preferred one"
      breadcrumbPath="Analysis of Alternatives"
    >
      <DemandSelector currentDemandId={demandId} stage="discovery" onDemandChange={setDemandId} />
    </DemandPageHeader>
  );

  if (!demandId) {
    return (
      <div>
        {header}
        <div style={{ padding: 40, textAlign: "center", color: "#8aa0b6" }}>
          No demand selected. Pick one above or from the Demand Dashboard.
        </div>
      </div>
    );
  }

  return (
    <div>
      {header}
      <div style={{ padding: "0 0 24px" }}>
        <AoAWorkbench demandId={demandId} demandTitle={title} onAdvance={onAdvance} />
      </div>
    </div>
  );
}
